import { useState, useEffect, useCallback } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import useApi from "../../hooks/useApi";
import { getId, idEquals } from "../../utils/idHelpers";

const UserSearch = ({
  value,
  onChange,
  label = "Search Users",
  disabled = false,
  size = "small",
}) => {
  const { postData } = useApi();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  // Sync external value prop: when parent clears selectedRep, clear selectedUser too
  useEffect(() => {
    if (!value) {
      setSelectedUser(null);
      setInputValue("");
    }
  }, [value]);

  // Debounce search to avoid excessive API calls
  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setOptions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await postData({
          entityName: "User",
          action: "searchUsers",
          data: { searchString: inputValue },
        });

        if (response?.isSuccessful && response.data) {
          setOptions(response.data);
        } else {
          setOptions([]);
        }
      } catch (error) {
        console.error("Error searching users:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const handleChange = (event, newValue) => {
    if (newValue) {
      setSelectedUser(newValue);
      onChange(getId(newValue));
    } else {
      setSelectedUser(null);
      onChange(null);
    }
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  return (
    <Autocomplete
      value={selectedUser}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={Array.isArray(options) ? options : []}
      getOptionLabel={(option) => option.name || ""}
      loading={loading}
      disabled={disabled}
      size={size}
      noOptionsText={
        inputValue.length < 2
          ? "Type at least 2 characters to search"
          : "No users found"
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

export default UserSearch;
